import * as React from 'react';
import { createPortal } from 'react-dom';

import {
  AnalyticsEvent,
  AnalyticsEventObj,
  AnalyticsSecondaryType,
  EVENT_PREFIX_FOR_SECONDARY_COUNTER,
  EVENTS_FOR_SECONDARY_COUNTER
} from 'shared/entities/analytics';
import { PORTAL_IDS } from 'shared/entities/app';
import { convertStringToNumber } from 'shared/entities/common/utils';
import { AnalyticsModel } from 'shared/models/analytics/AnalyticsModel';

/**
 * Получение id яндекс метрики для счетчика "Smartbotpro сервис" (auth.smartbotpro.ru)
 */
const YM_CONTAINER = process.env.IS_PROD
  ? '88450674'
  : (process.env.YM_CONTAINER as string);

/**
 * Получение id яндекс метрики для счетчика с лендинга "Smartbotpro"
 */
const YM_CONTAINER_SECONDARY = process.env.IS_PROD
  ? '88044383'
  : (process.env.YM_CONTAINER_SECONDARY as string);
export default class YandexAnalytics extends AnalyticsModel {
  protected get enabled(): boolean {
    return true;
  }

  private secondaryPixelInjected = false;

  private get ymContainer(): number {
    return convertStringToNumber(YM_CONTAINER);
  }

  /**
   * Возвращает вторичный счетчик если он настроен
   */
  private get ymSecondaryContainer(): number | null {
    if (!YM_CONTAINER_SECONDARY || YM_CONTAINER_SECONDARY === '""') {
      return null;
    }

    const secondaryContainer = convertStringToNumber(YM_CONTAINER_SECONDARY);
    return secondaryContainer || null;
  }

  protected executeScript(onSuccess: () => void, onError: () => void): void {
    const ymContainerNode = document.getElementById(PORTAL_IDS.YANDEX_METRIKA);

    if (!ymContainerNode) {
      return;
    }

    (function (m, e, t, r, i, k, a) {
      m[i] =
        m[i] ||
        function () {
          // eslint-disable-next-line prefer-rest-params
          (m[i].a = m[i].a || []).push(arguments);
        };
      // @ts-ignore
      m[i].l = 1 * new Date();
      // @ts-ignore
      k = e.createElement(t);
      // @ts-ignore
      a = e.getElementsByTagName(t)[0];
      // @ts-ignore
      k.async = 1;
      // @ts-ignore
      k.src = r;
      if (k) {
        // @ts-ignore
        k.onload = onSuccess;
        // @ts-ignore
        k.onerror = onError;
      }
      // @ts-ignore
      a.parentNode.insertBefore(k, a);
    })(window, document, 'script', 'https://mc.yandex.ru/metrika/tag.js', 'ym');

    // Создание пиксельной картинки для основного счетчика всегда
    createPortal(
      <div>
        <img
          src={`https://mc.yandex.ru/watch/${YM_CONTAINER}`}
          style={{
            position: 'absolute',
            left: '-9999px'
          }}
          alt=""
        />
      </div>,
      ymContainerNode
    );

    // Инициализация основного счетчика с полным трекингом
    window.ym(this.ymContainer, 'init', {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true
    });

    // Инициализация вторичного счетчика без автоматического трекинга (только для reachGoal)
    const secondary = this.ymSecondaryContainer;
    if (secondary && secondary !== this.ymContainer) {
      window.ym(secondary, 'init', {
        clickmap: false,
        trackLinks: false,
        accurateTrackBounce: false,
        webvisor: false,
        defer: true // Отключает автоматическую отправку просмотров страниц (hits)
      });
    }
  }

  sendEvent(event: AnalyticsEventObj): void {
    // Отправка в основной счетчик (всегда)
    window.ym(this.ymContainer, 'reachGoal', event.type, event.payload || {});

    this.sendEventToSecondaryCounter(event);
  }

  /**
   * Получение id яндекс метрики
   * @returns {Promise<BaseResponse<string>>}
   */
  getId = async (): Promise<BaseResponse<string>> => {
    if (!this.initialized.value) {
      return {
        isError: true
      };
    }

    return new Promise((res) => {
      window.ym(this.ymContainer, 'getClientID', (id) =>
        res({
          isError: false,
          data: id
        })
      );
    });
  };

  private sendEventToSecondaryCounter(event: AnalyticsEventObj) {
    // Отправка во вторичный счетчик только для избранных событий с префиксом
    const secondary = this.ymSecondaryContainer;
    if (!secondary || !EVENTS_FOR_SECONDARY_COUNTER.has(event.type)) {
      return;
    }

    this.ensureSecondaryPixel();

    const eventName = this.getEventTypeForSecondaryCounter(event.type);
    window.ym(secondary, 'reachGoal', eventName, event.payload || {});
  }

  private getEventTypeForSecondaryCounter(
    type: AnalyticsEvent
  ): AnalyticsSecondaryType {
    return `${EVENT_PREFIX_FOR_SECONDARY_COUNTER}_${type}`;
  }

  /**
   * Создание пиксельной картинки для вторичного счетчика
   */
  private ensureSecondaryPixel() {
    if (this.secondaryPixelInjected) {
      return;
    }

    const secondary = this.ymSecondaryContainer;
    if (!secondary) {
      return;
    }

    const img = document.createElement('img');
    img.src = `https://mc.yandex.ru/watch/${secondary}`;
    img.style.position = 'absolute';
    img.style.left = '-9999px';
    img.style.width = '1px';
    img.style.height = '1px';
    img.alt = '';

    document.body.appendChild(img);
    this.secondaryPixelInjected = true;
  }
}
